"use client";

import { cn } from "./utils";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React from "react";

type ScrollAreaScrollbarMode = "vertical" | "horizontal" | "both";

type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    /** Which scrollbars to show. Default `vertical` preserves previous behaviour. */
    scrollbars?: ScrollAreaScrollbarMode;
    /** Merged into the viewport; use e.g. `!h-auto` when the root should grow with horizontal-only content. */
    viewportClassName?: string;
};

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
    ({ className, children, scrollbars = "vertical", viewportClassName, ...props }, ref) => {
        const showVertical = scrollbars === "vertical" || scrollbars === "both";
        const showHorizontal = scrollbars === "horizontal" || scrollbars === "both";
        const showCorner = showVertical && showHorizontal;

        return (
            <ScrollAreaPrimitive.Root ref={ref} className={cn("relative overflow-hidden", className)} {...props}>
                <ScrollAreaPrimitive.Viewport className={cn("rounded-[inherit] w-full h-full", viewportClassName)}>
                    {children}
                </ScrollAreaPrimitive.Viewport>
                {showVertical ? <ScrollBar orientation="vertical" /> : null}
                {showHorizontal ? <ScrollBar orientation="horizontal" /> : null}
                {showCorner ? <ScrollAreaPrimitive.Corner /> : null}
            </ScrollAreaPrimitive.Root>
        );
    }
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
    React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
    React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
        ref={ref}
        orientation={orientation}
        className={cn(
            "flex transition-colors touch-none select-none",
            orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
            orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
            className
        )}
        {...props}
    >
        <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 bg-slate-400 dark:bg-slate-600 rounded-full" />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
